import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import type { AwsConnectionTeaserResponse } from '@/hooks/cloudPricing/useAwsConnections';
import { useApiQuery } from '@/hooks/new_base_hooks';

const baseUrl = (process.env.NEXT_PUBLIC_API_URL || '/api/').replace(/\/?$/, '/');
const CONNECTIONS_URL = `${baseUrl}data-ingestion/gcp-connections/`;

export type GcpConnectionListItem = {
  id: number;
  sourceType: string;
  name: string;
  status: string;
  accountId?: string;
  gcpProjectId?: string;
  accountIdVerified?: boolean;
  accountLabel?: string;
  serviceAccountEmail?: string;
};

export type GcpConnectionStartResponse = {
  externalId: string;
  serviceAccountEmail: string;
  iamCommands: string[];
  consoleIamUrl: string;
  status: string;
  dataSourceId?: number;
};

export type GcpConnectionStatusResponse = {
  externalId: string;
  status: string;
  statusMessage: string;
  accountId?: string;
  gcpProjectId?: string;
  accountIdVerified?: boolean;
  accountLabel?: string;
  serviceAccountEmail?: string;
  billingExportPending?: boolean;
  timestamps: {
    creatingConnectionConfigAt: string | null;
    awaitingCredentialsAt: string | null;
    accountReportedAt: string | null;
    readyAt: string | null;
    failedAt: string | null;
  };
};

export type GcpVerifyPayload = {
  projectId: string;
  billingExportProject: string;
  billingExportDataset: string;
  billingAccountId: string;
};

export function useGcpConnections(enabled = true) {
  return useApiQuery<{ results: GcpConnectionListItem[] }>({
    queryKey: ['data-ingestion', 'gcp-connections'],
    enabled,
  });
}

export function useStartGcpConnection() {
  return useMutation({
    mutationFn: async (_payload?: void): Promise<GcpConnectionStartResponse> => {
      const { data } = await axios.post<GcpConnectionStartResponse>(CONNECTIONS_URL, {}, { withCredentials: true });
      return data;
    },
  });
}

export function useGcpConnectionStatus(externalId?: string | null) {
  return useQuery({
    queryKey: ['data-ingestion', 'gcp-connections', externalId, 'status'],
    enabled: Boolean(externalId),
    queryFn: async (): Promise<GcpConnectionStatusResponse> => {
      const { data } = await axios.get<GcpConnectionStatusResponse>(
        `${CONNECTIONS_URL}${externalId}/status/`,
        { withCredentials: true },
      );
      return data;
    },
    refetchInterval: (query) => (query.state.data?.status === 'ready' || query.state.data?.status === 'failed' ? false : 3000),
  });
}

function isTerminalConnectionStatus(status?: string | null): boolean {
  return status === 'ready' || status === 'failed';
}

function isTeaserAnalyzing(teaser?: AwsConnectionTeaserResponse): boolean {
  if (!teaser || teaser.claimed) return false;
  if (!isTerminalConnectionStatus(teaser.connectionStatus)) return true;
  if (teaser.connectionStatus === 'failed') return false;
  const waste = teaser.waste?.status;
  const commitment = teaser.commitment?.status;
  const cloud = teaser.cloud?.status;
  return (
    waste == null ||
    commitment == null ||
    cloud == null ||
    waste === 'pending' ||
    waste === 'running' ||
    commitment === 'pending' ||
    commitment === 'running' ||
    cloud === 'pending' ||
    cloud === 'running'
  );
}

export function useGcpConnectionTeaser(externalId?: string | null) {
  return useQuery({
    queryKey: ['data-ingestion', 'gcp-connections', externalId, 'teaser'],
    enabled: Boolean(externalId),
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 404) return false;
      return failureCount < 2;
    },
    queryFn: async (): Promise<AwsConnectionTeaserResponse> => {
      const { data } = await axios.get<AwsConnectionTeaserResponse>(
        `${CONNECTIONS_URL}${externalId}/teaser/`,
        { withCredentials: true },
      );
      return data;
    },
    refetchInterval: (query) => {
      const teaser = query.state.data;
      if (!teaser || teaser.claimed) return false;
      if (!isTerminalConnectionStatus(teaser.connectionStatus)) return 3000;
      return isTeaserAnalyzing(teaser) ? 5000 : false;
    },
  });
}

export function useVerifyGcpConnection() {
  return useMutation({
    mutationFn: async ({
      externalId,
      payload,
    }: {
      externalId: string;
      payload: GcpVerifyPayload;
    }): Promise<GcpConnectionStatusResponse> => {
      const { data } = await axios.post<GcpConnectionStatusResponse>(
        `${CONNECTIONS_URL}${externalId}/verify/`,
        payload,
        { withCredentials: true },
      );
      return data;
    },
  });
}

export function useClaimGcpConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (externalId: string): Promise<GcpConnectionListItem> => {
      const { data } = await axios.post<GcpConnectionListItem>(
        `${CONNECTIONS_URL}${externalId}/claim/`,
        {},
        { withCredentials: true },
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['data-ingestion', 'gcp-connections'] });
    },
  });
}
